import { useMutation, useQuery } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { queryClient } from "../query";
import {
  createPerson,
  deletePerson,
  fetchChallengeRanking,
  fetchFeatureFlags,
  fetchFieldGames,
  fetchFields,
  fetchGameActions,
  fetchGames,
  fetchPlayersByStat,
  fetchRanking,
  fetchRegister,
  fetchSponsors,
  fetchTeamById,
  fetchTeamPersons,
  fetchTeams,
  fetchTeamsByStat,
  fetchTournamentOptions,
  registerTeam,
  updatePerson,
  updateRegister,
  uploadAttachment,
} from "./airtable";
import { SavePersonInput } from "./types";

export const useFetchSponsors = () =>
  useQuery({
    queryKey: ["sponsors"],
    queryFn: () => fetchSponsors(),
  });

export const useFetchTournamentOptions = (dayPart: "am" | "pm") =>
  useQuery({
    queryKey: ["tournament-options", dayPart],
    queryFn: () => fetchTournamentOptions(dayPart),
  });

export const useFetchRanking = (params: {
  selectedCategory: string | undefined;
  dayPart: "am" | "pm";
}) =>
  useQuery({
    queryKey: ["ranking", params],
    queryFn: () => fetchRanking(params),
  });

export const useFetchGames = (params: {
  selectedCategory: string | undefined;
  dayPart: "am" | "pm";
}) =>
  useQuery({
    queryKey: ["game", params],
    queryFn: () => fetchGames(params),
  });

export const useFetchChallengeRanking = () =>
  useQuery({
    queryKey: ["challenge-ranking"],
    queryFn: () => fetchChallengeRanking(),
  });

export const useFetchFields = () =>
  useQuery({
    queryKey: ["fields"],
    queryFn: () => fetchFields(),
  });

export const useFetchFieldGames = ({
  field,
  dayPart = "am",
}: {
  field?: string;
  dayPart?: "am" | "pm";
}) =>
  useQuery({
    queryKey: ["field-games", { field, dayPart }],
    queryFn: () => fetchFieldGames({ field, dayPart }),
    staleTime: 30 * 1000,
  });

export const useFetchFeatureFlags = () =>
  useQuery({
    queryKey: ["feature-flags"],
    queryFn: () => fetchFeatureFlags(),
  });

export const useRegisterTeam = () => {
  return useMutation({
    mutationFn: async (data: any) => {
      if (data.recordId) {
        return updateRegister(data);
      }
      return registerTeam(data);
    },
    async onSuccess(data) {
      enqueueSnackbar("Inscription enregistrée", { variant: "success" });
      return data;
    },
    async onError(error) {
      enqueueSnackbar("Erreur lors de l'enregistrement de votre équipe", {
        variant: "error",
      });
      return error;
    },
  });
};

export const useFetchRegister = (teamId: string | null | undefined) => {
  return useQuery({
    queryKey: ["register"],
    queryFn: () => fetchRegister(teamId),
    enabled: Boolean(teamId),
  });
};

export const useFetchTeamPersons = (teamId: string | null | undefined) => {
  return useQuery({
    queryKey: ["team-persons", teamId],
    queryFn: () => fetchTeamPersons(teamId),
    enabled: Boolean(teamId),
  });
};

export const useSavePerson = () => {
  return useMutation({
    mutationFn: async ({
      recordId,
      image,
      autorisation,
      ...data
    }: SavePersonInput) => {
      if (recordId) {
        return updatePerson(recordId, data);
      }
      return createPerson(data);
    },
    async onSuccess(data, variables) {
      const recordId = data?.id;
      if (!recordId) {
        return data;
      }
      if (variables?.image) {
        await uploadAttachment(recordId, "image", variables.image);
      }
      if (variables?.autorisation) {
        await uploadAttachment(
          recordId,
          "autorisation",
          variables.autorisation
        );
      }
      if (variables?.licence) {
        await uploadAttachment(recordId, "licence", variables.licence);
      }
      enqueueSnackbar("Membre ajouté", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["team-persons"] });
      return data;
    },
    async onError(error) {
      enqueueSnackbar("Erreur lors de l'ajout du membre", { variant: "error" });
      return error;
    },
  });
};

export const useDeletePerson = () => {
  return useMutation({
    mutationFn: async (id: string) => {
      return deletePerson(id);
    },
    async onSuccess(data) {
      enqueueSnackbar("Membre supprimé", { variant: "success" });
      await queryClient.invalidateQueries({ queryKey: ["team-persons"] });
      return data;
    },
    async onError(error) {
      enqueueSnackbar("Erreur lors de la suppresion du membre", {
        variant: "error",
      });
      return error;
    },
  });
};

export const useFetchGameActions = (gameId: string | undefined) => {
  return useQuery({
    queryKey: ["game-actions", gameId],
    queryFn: () => fetchGameActions(gameId),
    enabled: Boolean(gameId),
  });
};

export const useFetchPlayersByStats = (
  stat: "tries" | "conversions" | "penalties" | "drops" | "points"
) => {
  return useQuery({
    queryKey: ["players-by-stats", stat],
    queryFn: () => fetchPlayersByStat(stat),
  });
};

export const useFetchTeamsByStats = (
  stat: "points_scored" | "points_taken"
) => {
  return useQuery({
    queryKey: ["teams-by-stats", stat],
    queryFn: () => fetchTeamsByStat(stat),
  });
};

export const useFetchTeams = () => {
  return useQuery({
    queryKey: ["teams"],
    queryFn: () => fetchTeams(),
  });
};

export const useFetchTeamById = (id: string | undefined) => {
  return useQuery({
    queryKey: ["team", id],
    queryFn: () => fetchTeamById(id || ""),
    enabled: Boolean(id),
  });
};
